import React from 'react';
import Select from 'react-select';
import { formatDisplayValue, validateFieldValue } from '../config/fieldConfig';
import FieldTooltip from './FieldTooltip';
import YesNoToggle from './YesNoToggle';

/**
 * react-select paints the selected option a saturated blue and relies on its
 * own white text for contrast. Our option markup comes from formatOptionLabel,
 * which sets its own teal title and grey description -- those colours do not
 * invert, so on the blue they became unreadable.
 *
 * A light brand tint keeps the row obviously selected while leaving every
 * colour in the label legible, whatever the label chooses to render.
 */
const optionStyles = (provided, state) => ({
  ...provided,
  backgroundColor: state.isSelected
    ? 'rgba(var(--nsi-brand-rgb), 0.12)'
    : state.isFocused
      ? '#f1f3f5'
      : 'transparent',
  color: 'inherit',
  fontWeight: state.isSelected ? 600 : 'inherit',
  cursor: 'pointer',
  ':active': {
    backgroundColor: 'rgba(var(--nsi-brand-rgb), 0.2)'
  }
});

/**
 * Universal Field Renderer Component
 * 
 * This component can render any field type based on the centralized field configuration.
 * It handles all field types, validation, and custom behaviors in one place.
 */
const FieldRenderer = ({
  fieldId,
  config,
  value,
  isEditing = false,
  isRequired = false,
  isSaving = false,
  onSave,
  onStartEdit,
  onStopEdit,
  additionalData = {} // For navigate function, etc.
}) => {

  // Handle field save
  const handleSave = (newValue) => {
    // Validate the value
    const validation = validateFieldValue(fieldId, newValue, config);
    if (!validation.isValid) {
      // Could show validation errors here
      console.warn(`Validation failed for ${fieldId}:`, validation.errors);
      return;
    }

    // Handle custom onChange (like category clearing subcategory)
    if (config.onChange) {
      config.onChange({ value: newValue });
    }

    // Save the value
    if (onSave) {
      onSave(fieldId, newValue);
    }

    // Stop editing
    if (onStopEdit) {
      onStopEdit();
    }
  };

  // Handle key events for text inputs
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && config.type !== 'textarea') {
      e.preventDefault();
      handleSave(e.target.value);
    } else if (e.key === 'Escape') {
      onStopEdit?.();
    }
  };

  // Handle blur for text inputs
  const handleBlur = (e) => {
    if (config.type === 'text' || config.type === 'textarea' || config.type === 'number') {
      const newValue = e.target.value;
      if (newValue !== value) {
        handleSave(newValue);
      } else {
        onStopEdit?.();
      }
    }
  };

  // Handle select changes
  const handleSelectChange = (selectedOption) => {
    let newValue;
    if (config.type === 'multiselect' || config.isMulti) {
      newValue = selectedOption ? selectedOption.map(opt => opt.value) : [];
    } else {
      newValue = selectedOption ? selectedOption.value : null;
    }
    handleSave(newValue);
  };

  // Render edit field
  const renderEditField = () => {
    const { type, options, isClearable, isDisabled } = config;
    
    // Use the formatOptionLabel from config (shared for all select types)
    const formatOptionLabel = config.formatOptionLabel;

    switch (type) {
      case 'textarea':
        return (
          <textarea
            className="inline-edit-field"
            defaultValue={value || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            rows={3}
            autoFocus
          />
        );

      case 'yesno':
        // A sliding switch, not a dropdown. Custom yesno fields never get
        // options built for them, so a Select here rendered an empty menu.
        return (
          <YesNoToggle
            value={value}
            onChange={handleSave}
            disabled={isDisabled}
            label={config.label}
          />
        );

      case 'select':
        const currentOption = options?.find(opt => opt.value === value) || null;
        
        return (
          <Select
            className="inline-edit-select"
            value={currentOption}
            onChange={handleSelectChange}
            options={options}
            isClearable={isClearable}
            isDisabled={isDisabled}
            placeholder={`Select ${config.label.toLowerCase()}...`}
            formatOptionLabel={formatOptionLabel}
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: '38px',
                border: 'none',
                boxShadow: 'none',
                background: '#f8f9fa',
                borderRadius: '3px'
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999
              }),
              option: optionStyles,
              dropdownIndicator: (provided) => ({
                ...provided,
                color: '#6c757d'
              })
            }}
          />
        );

      case 'multiselect':
        const currentOptions = Array.isArray(value) 
          ? options?.filter(opt => value.includes(opt.value)) || []
          : [];
        
        const availableOptions = options?.filter(opt => !value?.includes(opt.value)) || [];

        return (
          <Select
            className="inline-edit-select"
            value={currentOptions}
            onChange={handleSelectChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                onStopEdit?.();
              }
            }}
            options={availableOptions}
            isMulti={true}
            isClearable={isClearable}
            isSearchable={true}
            isDisabled={config.isDisabled}
            placeholder={`Select ${config.label.toLowerCase()}...`}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            formatOptionLabel={formatOptionLabel}
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: '38px',
                border: 'none',
                boxShadow: 'none',
                background: state.isDisabled ? '#e9ecef' : '#f8f9fa',
                borderRadius: '3px',
                cursor: state.isDisabled ? 'not-allowed' : 'default'
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999
              }),
              option: (provided, state) => ({
                ...optionStyles(provided, state),
                padding: fieldId === 'child_of' ? '8px 12px' : provided.padding
              }),
              multiValue: (provided) => ({
                ...provided,
                backgroundColor: '#e3f2fd',
                borderRadius: '12px'
              })
            }}
            // Add custom components if specified in config
            components={config.customComponents?.selectComponents}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className="inline-edit-field"
            defaultValue={value || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            min={config.validation?.min}
            max={config.validation?.max}
            autoFocus
          />
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            className="inline-edit-field"
            defaultValue={value || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            maxLength={config.validation?.maxLength}
            autoFocus
          />
        );
    }
  };

  // Render display value
  const renderDisplayValue = () => {
    return formatDisplayValue(fieldId, value, config, additionalData);
  };

  const isReadonly = config.type === 'readonly';
  const showAsAlwaysEditing = (config.type === 'select' || config.type === 'multiselect' || config.type === 'yesno') && !isReadonly;

  return (
    <div className="detail-field">
      <label>
        <span className="field-label-text">
          {config.label}
          {isRequired && <span className="required-indicator">*</span>}
          {config.isCustom && <span className="custom-field-badge">Custom</span>}
          {config.tooltip && <FieldTooltip text={config.tooltip} />}
        </span>
      </label>
      <div className={`field-value-container ${!isReadonly ? 'editable' : ''} ${isEditing ? 'editing' : ''} ${showAsAlwaysEditing ? 'always-editing' : ''}`}>
        {isEditing && !isReadonly ? (
          renderEditField()
        ) : (
          showAsAlwaysEditing ? (
            renderEditField()
          ) : (
            <div 
              className="field-value"
              onClick={() => !isReadonly && onStartEdit?.()}
            >
              {renderDisplayValue()}
            </div>
          )
        )}
        {isSaving && (
          <div className="field-spinner">
            <i className="bi bi-arrow-clockwise spin"></i>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldRenderer;